import PromiseQueue from 'promise-queue';
import { AnyAction } from 'redux';
import { fetchAccessTokenIfNeeded, invokeWithAccessToken } from '~/AccessToken';
import { Product, Query } from '~/Api/Product';
import { retryFetchAfterUserProductGroupLock, UserProductGroup } from '~/Api/User/ProductGroup';
import {
	addProductsBySearch as apiAddProductsBySearch,
	addProductsToUserGroup as apiAddProductsToUserGroup,
	findItem,
	findUserGroupProducts,
	ItemErrorResponse,
	Meta,
	ProductsResult,
	removeProductsFromUserGroup as apiRemoveProductsFromUserGroup,
	UpdateErrorResponse,
	updateProductPackageSizeForSingleUnit as apiUpdateProductPackageSizeForSingleUnit,
} from '~/Api/User/ProductGroup/Product';
import { invokeApi, resolve } from '~/ApiClient';
import { fetchCountriesIfNeeded } from '~/Country';
import { handleError } from '~/ErrorHandler';
import { flashWarningMessage, removeFlashMessageByText } from '~/FlashMessage/actions';
import { processQuery } from '~/Product';
import { ThunkAction, ThunkDispatch } from '~/Store';

import ActionTypes from './ActionTypes';
import { ReceiveAddToGroupAction, selectItemByGroupIdAndId, selectItemsByGroupId, State } from './reducer';

export enum SelectionTypes {
	all = 'all',
	products = 'products',
}

export interface ProductsSelection {
	type: SelectionTypes.products;
	products: Set<Product>;
	query: Query;
}

export interface AllProductsSelection {
	type: SelectionTypes.all;
	query: Query;
}

export type Selection = ProductsSelection | AllProductsSelection;

export function fetchItems(
	groupId: string,
	page = 1,
	perPage: number | null = null
): ThunkAction<Promise<ProductsResult>, any> {
	return async (dispatch, getState) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(
			invokeApi(
				findUserGroupProducts,
				userId,
				groupId,
				page,
				perPage !== null ? perPage : getState().groupProduct.itemsPerPage
			)
		);
		dispatch({ type: ActionTypes.requestItems, request, groupId });

		return resolve(request, (response) => {
			dispatch({
				type: ActionTypes.receiveItems,
				request,
				groupId,
				items: response ? response.items : null,
				pagination: response ? response.pagination : null,
			});
		});
	};
}

export function fetchMoreItems(groupId: string): ThunkAction<Promise<ProductsResult | null>, any> {
	return async (dispatch, getState) => {
		const items = selectItemsByGroupId(getState().groupProduct, groupId);
		if (!items || !items.pagination) {
			return dispatch(fetchItems(groupId));
		}

		const nextPage = items.pagination?.nextGrowedPage;
		if (nextPage === null || nextPage === undefined) {
			return null;
		}

		return dispatch(fetchItems(groupId, nextPage.number, nextPage.perPage));
	};
}

export function fetchItem(groupId: string, productId: string): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findItem, userId, groupId, productId));
		dispatch({ type: ActionTypes.requestItem });

		try {
			const { productGroupItem: item } = await request;
			dispatch({ type: ActionTypes.receiveItem, groupId, productId, item });
		} catch (error) {
			dispatch({
				type: ActionTypes.receiveItem,
				groupId,
				productId,
				item: error instanceof ItemErrorResponse && error.handleProductGroupItemNotFound() ? null : undefined,
			});
			dispatch(handleError(error));
		}
	};
}

export function cachedFetchItem(groupId: string, productId: string): ThunkAction<Promise<void>, any> {
	return async (dispatch, getState) => {
		if (!selectItemByGroupIdAndId(getState().groupProduct, groupId, productId)) {
			await dispatch(fetchItem(groupId, productId));
		}
	};
}

export function addSelectionToGroup(group: UserProductGroup, selection: Selection): ThunkAction<Promise<void>, any> {
	return async (dispatch, getState) => {
		const request =
			selection.type === 'all'
				? dispatch(addProductsBySearch(group, selection.query))
				: dispatch(
						addProductsToGroup(group, Array.from(selection.products), new Meta(selection.query.filterParameters))
				  );
		await request;

		dispatch(
			invokeWithAccessToken((accessToken) => {
				const { recentStorage } = getState().productGroup;
				if (group.parent) {
					recentStorage.addGroupId(accessToken.user.id, group.parent.id);
				} else if (group.isGroup()) {
					recentStorage.addGroupId(accessToken.user.id, group.id);
				}
			})
		);
	};
}

export function addProductsToGroup(
	group: UserProductGroup,
	products: Product[],
	meta: Meta | null
): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		await dispatch(
			withTimeout(async () => {
				const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
				const request = retryFetchAfterUserProductGroupLock(() =>
					dispatch(
						invokeApi(
							apiAddProductsToUserGroup,
							userId,
							group.id,
							products.map((product) => ({
								id: product.id,
								packageSizeForSingleUnit: null,
							})),
							meta
						)
					)
				);
				dispatch({ type: ActionTypes.requestAddToGroup, group });

				await resolve(request, () => {
					dispatch<ReceiveAddToGroupAction>({ type: ActionTypes.receiveAddToGroup, group, products });
				});
			})
		);
	};
}

function addProductsBySearch(group: UserProductGroup, query: Query): ThunkAction<Promise<void>, any> {
	return async (dispatch: ThunkDispatch<AnyAction>) => {
		await dispatch(
			withTimeout(async () => {
				const requiresCountries =
					query.filterParameters.find((parameter) => parameter.name === 'country') !== undefined;
				const countries = requiresCountries ? (await dispatch(fetchCountriesIfNeeded()))?.items || [] : [];

				const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
				const request = retryFetchAfterUserProductGroupLock(() =>
					dispatch(invokeApi(apiAddProductsBySearch, userId, group.id, processQuery(query)))
				);
				dispatch({ type: ActionTypes.requestAddToGroupBySearch, group });
				await resolve(request, () => dispatch({ type: ActionTypes.receiveAddToGroupBySearch, group }));
			})
		);
	};
}

export function removeFromGroup(groupId: string, products: Product[] | string[]): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(
				invokeApi(
					apiRemoveProductsFromUserGroup,
					userId,
					groupId,
					products.map((product) => (typeof product === 'string' ? product : product.id))
				)
			)
		);
		dispatch({ type: ActionTypes.requestRemoveFromGroup, request, groupId });

		await resolve(request, () =>
			dispatch({
				type: ActionTypes.receiveRemoveFromGroup,
				request,
				groupId,
				products,
			})
		);
	};
}

export const SlowSaveMessage = 'Saving is taking unusually long time, please be patient';

function withTimeout(action: () => Promise<void>): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const slowMessageTimeoutId = setTimeout(() => {
			dispatch(flashWarningMessage(SlowSaveMessage, false));
		}, 5 * 1000);

		try {
			await action();
		} finally {
			clearTimeout(slowMessageTimeoutId);
			dispatch(removeFlashMessageByText(SlowSaveMessage));
		}
	};
}

const updateQueue = new PromiseQueue(1, Infinity);

export function markProductPackageSizeForSingleUnitChanged(groupId: string, productId: string): AnyAction {
	return {
		type: ActionTypes.markPackageSizeForSingleUnitChanged,
		groupId,
		productId,
	};
}

export function updateProductPackageSizeForSingleUnit(
	groupId: string,
	productId: string,
	packageSizeForSingleUnit: string | null,
	afterAction?: () => Promise<any>
): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		return updateQueue.add(() =>
			dispatch(
				immediatelyUpdateProductPackageSizeForSingleUnit(groupId, productId, packageSizeForSingleUnit, afterAction)
			)
		);
	};
}

function immediatelyUpdateProductPackageSizeForSingleUnit(
	groupId: string,
	productId: string,
	packageSizeForSingleUnit: string | null,
	afterAction?: () => Promise<any>
): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(
			invokeApi(apiUpdateProductPackageSizeForSingleUnit, userId, groupId, productId, packageSizeForSingleUnit)
		);
		dispatch({
			type: ActionTypes.requestUpdatePackageSizeForSingleUnit,
			groupId,
			productId,
		});

		try {
			const { productGroupItem: item } = await request;
			if (afterAction) {
				await afterAction();
			}
			dispatch({
				type: ActionTypes.receiveUpdatePackageSizeForSingleUnit,
				groupId,
				productId,
				item,
			});
		} catch (error) {
			dispatch({
				type: ActionTypes.receiveUpdatePackageSizeForSingleUnit,
				groupId,
				productId,
				item: null,
			});

			if (error instanceof UpdateErrorResponse && error.handleProductGroupItemNotFound()) {
				dispatch(pruneProducts(groupId));
				dispatch(fetchItems(groupId));
				return;
			}
			dispatch(handleError(error));
		}
	};
}

export function pruneProducts(groupId: string): { type: string; groupId: string } {
	return { type: ActionTypes.prune, groupId };
}

export function setupItemsPerPage(perPage: number): { type: string; perPage: number } {
	return { type: ActionTypes.setupItemsPerPage, perPage };
}
